import axios from "axios";
import { enqueue, newId } from "@/lib/outbox";
import { flushOutbox, requestBackgroundSync } from "@/lib/sync";
import { isTrainingModeActive } from "@/contexts/TrainingContext";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "haccp_access_token";
const REFRESH_KEY = "haccp_refresh_token";

export function setAccessToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getAccessToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setRefreshToken(token) {
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}
export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}
export function clearAuthTokens() {
  setAccessToken(null);
  setRefreshToken(null);
}

const api = axios.create({
  baseURL: `${BASE}/api`,
  // withCredentials is intentionally false: the upstream edge proxy injects
  // `Access-Control-Allow-Origin: *`, which browsers refuse to combine with
  // credentials. We rely on Authorization: Bearer + localStorage instead so
  // auth works in every preview/proxy/cross-origin scenario.
  withCredentials: false,
  timeout: 15000,
});

// Endpoints that are safe to enqueue offline (writes that staff record from the kitchen)
const QUEUEABLE_PATTERNS = [
  /^\/temperature-logs/,
  /^\/receival/,
  /^\/cleaning\/logs/,
  /^\/checklists\/logs/,
  // /labels* is queueable EXCEPT the two thermal-print endpoints:
  //  - /labels/{id}/print-thermal   (saved-label print)
  //  - /labels/print-thermal-adhoc  (test print, no saved label)
  //  - /labels/render-tspl          (WebUSB byte render — needs live server)
  // Queueing any would spuriously mint a synthetic 200 offline and
  // replay the print job when the tablet reconnects hours later.
  /^\/labels(?!\/.+\/print-thermal|\/print-thermal-adhoc|\/render-tspl)/,
  /^\/pest-logs/,
  /^\/training/,
  /^\/haccp\/corrective-actions/,
];

function isQueueable(method, url) {
  if (!method || method.toUpperCase() !== "POST") return false;
  return QUEUEABLE_PATTERNS.some((rx) => rx.test(url || ""));
}

function isNetworkError(err) {
  if (!err) return false;
  if (err.code === "ECONNABORTED") return true;
  if (err.message === "Network Error") return true;
  return !err.response;
}

// ---------- Silent JWT refresh helpers ----------
// `refreshInflight` de-dupes concurrent /auth/refresh calls. A burst of
// parallel 401s (e.g. dashboard + sites + equipment fired on mount) will
// all await the same single refresh round-trip.
let refreshInflight = null;

async function refreshAccessToken() {
  if (refreshInflight) return refreshInflight;
  const rt = getRefreshToken();
  if (!rt) return null;
  // Use a bare axios call (no interceptors) so a 401 here doesn't loop.
  refreshInflight = axios
    .post(`${BASE}/api/auth/refresh`, { refresh_token: rt }, {
      headers: { "Content-Type": "application/json" },
      withCredentials: false,
      timeout: 10000,
    })
    .then((r) => {
      const next = r.data?.access_token;
      const nextRefresh = r.data?.refresh_token;
      if (next) setAccessToken(next);
      if (nextRefresh) setRefreshToken(nextRefresh);
      return next || null;
    })
    .catch(() => {
      // Refresh failed → session is truly dead, clear local tokens so the
      // 401 handler downstream can route to /login.
      clearAuthTokens();
      return null;
    })
    .finally(() => {
      refreshInflight = null;
    });
  return refreshInflight;
}

api.interceptors.request.use((config) => {
  // ---------- Training-mode short-circuit ----------
  // When the user has flipped Training Mode on in the topbar, every
  // mutation (POST/PATCH/PUT/DELETE) is diverted to a fake response so
  // new staff can practise on the real UI without writing to the audit
  // ledger. Reads still pass through so dashboards/lists look populated.
  const method = (config.method || "get").toLowerCase();
  if (isTrainingModeActive() && method !== "get") {
    // axios supports cancelling via a CancelToken / signal. We instead
    // throw a tagged error that the response interceptor catches and
    // converts into a synthesized success response — this keeps every
    // call-site (which awaits `data`) unchanged.
    const e = new Error("training-mode-short-circuit");
    e._training = { method, url: config.url, data: config.data, params: config.params };
    throw e;
  }
  // For queueable POSTs without a body.id, mint one client-side so server
  // and client agree on a stable id even before sync.
  if (
    isQueueable(config.method, config.url) &&
    config.data &&
    typeof config.data === "object" &&
    !config.data.id
  ) {
    config.data.id = newId();
  }
  // Attach Bearer token when present — fallback path for environments
  // where the edge proxy strips/rewrites credentialed-cookie CORS headers.
  const tok = getAccessToken();
  if (tok && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // ---------- Training-mode synthetic success ----------
    if (err && err._training) {
      const body = err._training.data;
      const fakeRow = {
        id: (body && body.id) || newId(),
        ...(typeof body === "object" && body !== null ? body : {}),
        created_at: new Date().toISOString(),
        _training: true,
      };
      return Promise.resolve({
        data: fakeRow,
        status: 200,
        statusText: "OK (training mode)",
        headers: {},
        config: err._training,
        _training: true,
      });
    }
    const cfg = err.config || {};
    const status = err?.response?.status;

    // ---------- Trial expired / subscription required ----------
    // Backend returns 402 with code "trial_expired" for writes on a site
    // whose 30-day trial has ended and has no active subscription. Surface a
    // global event so the app can nudge the admin to Billing.
    if (status === 402 && err?.response?.data?.code === "trial_expired") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("haccp-trial-expired", { detail: err.response.data }));
      }
      return Promise.reject(err);
    }

    // ---------- Silent JWT refresh ----------
    // When an access token expires mid-shift, the backend returns 401 on
    // any non-auth endpoint. Before bouncing the user to /login, try to
    // mint a fresh access token using the stored refresh token, then
    // replay the original request once. Refreshes are de-duped via a
    // module-level promise so a burst of parallel 401s only triggers one
    // round-trip.
    if (
      status === 401 &&
      !cfg.__retry &&
      !/\/auth\/(login|refresh|logout)$/.test(cfg.url || "") &&
      getRefreshToken()
    ) {
      cfg.__retry = true;
      try {
        const newAccess = await refreshAccessToken();
        if (newAccess) {
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${newAccess}`;
          return api.request(cfg);
        }
      } catch {
        // fall through to the existing 401 handler below
      }
    }

    // Session expired / unauthenticated. Don't let it surface as an
    // uncaught runtime error in every page's load() function — instead,
    // tell AuthContext to clear the user (so PrivateRoute routes to /login)
    // and resolve with an empty payload so the calling component renders
    // gracefully on its way out.
    if (status === 401 && !/\/auth\/(login|refresh|me)$/.test(cfg.url || "")) {
      clearAuthTokens();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("haccp-session-expired"));
      }
      return Promise.resolve({ data: Array.isArray(cfg.__shape) ? [] : null, _expired: true });
    }

    if (isNetworkError(err) && isQueueable(cfg.method, cfg.url)) {
      // Enqueue and resolve with a synthetic optimistic response so the UI
      // can keep moving. The server's eventual response replaces the row
      // when sync flushes.
      const optimisticId =
        (cfg.data && cfg.data.id) || newId();
      const optimistic = {
        id: optimisticId,
        ...((cfg.data && typeof cfg.data === "object") ? cfg.data : {}),
        site_id: cfg.params?.site_id,
        resource: (cfg.url || "").split("?")[0],
        created_at: new Date().toISOString(),
        _pending: true,
      };
      await enqueue({
        method: cfg.method,
        url: cfg.url,
        params: cfg.params,
        body: cfg.data,
        resource: optimistic.resource,
        site_id: cfg.params?.site_id,
        optimistic,
      });
      // Trigger a flush attempt soon (in case it's a transient hiccup)
      setTimeout(flushOutbox, 1500);
      // Ask the SW to wake us up the moment connectivity returns —
      // gives us pre-`online`-event reliability on mobile Safari.
      requestBackgroundSync();
      return Promise.resolve({ data: optimistic, _queued: true });
    }
    throw err;
  }
);

export function withSite(siteId, params = {}) {
  return { params: { site_id: siteId, ...params } };
}

export function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
